import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import type { EntityFieldValue, PreviewTree } from "./mappingUtils";

interface EntityPreviewProps {
  tree: PreviewTree;
  saveDisabled: boolean;
  // Omit in parse flow — saving is a train-only action.
  onSave?: () => void;
  saveLabel?: string;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

function FieldList({ values }: { values: EntityFieldValue[] }) {
  if (values.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No fields mapped.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {values.map((v) => (
        <Stack
          key={v.field}
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
        >
          <Typography
            variant="body2"
            sx={{
              width: 220,
              flexShrink: 0,
              color: "text.secondary",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {v.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: '"Roboto Mono", monospace',
              fontSize: "0.8125rem",
              wordBreak: "break-word",
              color: v.isDefault ? "text.secondary" : "inherit",
              fontStyle: v.isDefault ? "italic" : "normal",
            }}
          >
            {formatValue(v.value)}
            {v.isDefault && " (default)"}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export default function EntityPreview({
  tree,
  saveDisabled,
  onSave,
  saveLabel = "Looks Good — Save Template",
}: EntityPreviewProps) {
  const claimCount = tree.claims.length;
  const procedureCount = tree.claims.reduce(
    (acc, c) => acc + c.procedures.length,
    0,
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ marginBottom: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Entities Preview
        </Typography>
        <Chip
          size="small"
          label={`${claimCount} claim${claimCount === 1 ? "" : "s"}`}
        />
        <Chip
          size="small"
          label={`${procedureCount} procedure${procedureCount === 1 ? "" : "s"}`}
        />
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 2 }}
      >
        Resolved from the first values in the draft. Verify against the PDF on
        the left.
      </Typography>

      <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, marginBottom: 1 }}
        >
          Payment
        </Typography>
        <FieldList values={tree.payment} />
      </Paper>

      {tree.claims.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No claims bound. Map at least one Claim field to see the tree.
        </Typography>
      ) : (
        tree.claims.map((claim, idx) => (
          <Paper
            // biome-ignore lint/suspicious/noArrayIndexKey: display-only preview that never reorders
            key={`claim-${idx}`}
            variant="outlined"
            sx={{ padding: 2, marginBottom: 2, backgroundColor: "#FAFAFA" }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, marginBottom: 1 }}
            >
              Claim #{idx + 1}
            </Typography>
            <FieldList values={claim.fields} />

            {claim.patient && (
              <>
                <Divider sx={{ marginY: 1.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    display: "block",
                    marginBottom: 0.5,
                  }}
                >
                  PATIENT
                </Typography>
                <FieldList values={claim.patient} />
              </>
            )}

            {claim.procedures.length > 0 && (
              <>
                <Divider sx={{ marginY: 1.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    display: "block",
                    marginBottom: 0.5,
                  }}
                >
                  PROCEDURES ({claim.procedures.length})
                </Typography>
                <Stack spacing={1}>
                  {claim.procedures.map((proc, pIdx) => (
                    <Box
                      // biome-ignore lint/suspicious/noArrayIndexKey: display-only preview that never reorders
                      key={`proc-${pIdx}`}
                      sx={{
                        paddingLeft: 1.5,
                        borderLeft: "2px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", marginBottom: 0.5 }}
                      >
                        Procedure {pIdx + 1}
                      </Typography>
                      <FieldList values={proc} />
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        ))
      )}

      {onSave && (
        <Box sx={{ marginTop: 3 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={saveDisabled}
            onClick={onSave}
          >
            {saveLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
